import { normalizeName } from '../../shared/naming';

/**
 * O nome de uma tarefa do GanttPRO, separado em disciplina, serviço e local.
 *
 * O arquivo do Park Europa escreve o nome de **três jeitos**, e o `ganttpro` precisa ler os
 * três (medidos nas 452 tarefas da Torre 2):
 *
 * | Padrão                          | Exemplo                                              | Quantas |
 * | ------------------------------- | ---------------------------------------------------- | ------: |
 * | `SERVICE_LOCATION`              | `Estrutura / 2º Pavimento - Tipo`                    |     117 |
 * | `DISCIPLINE_SERVICE_LOCATION`   | `Revestimento Interno / Emboço / 1º Pavimento - Tipo` |     323 |
 * | `NO_LOCATION`                   | `Elevador`, `Cobertura - Metálica`                   |       8 |
 * | `DISCIPLINE_SERVICE` (invertido)| `Fundação / Execução de Gabarito`                    |       4 |
 *
 * Mais dois detalhes que o nome carrega: o **separador** entre disciplina e serviço varia
 * (`Instalação Hidráulica - Prumada & Distribuição`, com hífen, ao lado de
 * `Instalação Hidráulica / Louças`, com barra), e o **local** é de cinco espécies — andar
 * ("1º Pavimento - Tipo", "14º Barrilete"), muros ("Muros 1º Pavimento"), face da fachada
 * ("Frente", "Lateral Direita"), "Barrilete" sem número, e nenhum.
 *
 * O nome sozinho **não** diz a disciplina do sistema: quem diz é o caminho de grupos
 * (`groupPath`) mais o de-para. Esta função só lê o que está escrito — e usa o caminho
 * numa única decisão, a do padrão invertido, em que a esquerda é a disciplina.
 */

export type TaskNamePattern =
  'SERVICE_LOCATION' | 'DISCIPLINE_SERVICE_LOCATION' | 'NO_LOCATION' | 'DISCIPLINE_SERVICE';

/** As quatro faces da fachada, na grafia que vai para o trecho do item. */
export type FacadeFace = 'FRENTE' | 'FUNDOS' | 'LATERAL DIREITA' | 'LATERAL ESQUERDA';

export type TaskLocation =
  /** "1º Pavimento - Tipo", "14º Pavimento - Barrilete", "14º Barrilete", "Térreo". */
  | {
      readonly kind: 'FLOOR';
      readonly level: number;
      /** O que vem depois do hífen: "Tipo", "Barrilete", "Barrilete Teto". */
      readonly floorKind: string | null;
      readonly raw: string;
    }
  /** "Muros 1º Pavimento": os muros de um andar, que não são o andar. */
  | { readonly kind: 'WALLS'; readonly level: number; readonly raw: string }
  /** "Frente", "Fundos", "Lateral Direita", "Lateral Esquerda". */
  | { readonly kind: 'FACADE'; readonly face: FacadeFace; readonly raw: string }
  /** "Barrilete" sozinho, sem o número do andar. */
  | { readonly kind: 'UNNUMBERED_FLOOR'; readonly floorKind: string; readonly raw: string }
  /** O nome não traz local (e a tarefa se espalha pelos pavimentos — RN-326). */
  | { readonly kind: 'NONE' }
  /** Há um local escrito, mas não é nenhum dos acima: a conferência mostra. */
  | { readonly kind: 'UNKNOWN'; readonly raw: string };

export interface ParsedTaskName {
  readonly raw: string;
  readonly pattern: TaskNamePattern;
  /** O que o nome diz antes do serviço: "Instalação Especial" em `Instalação Especial / Gás`. */
  readonly discipline: string | null;
  /** O serviço como foi escrito: "Gás", "Prumada & Distribuição", "Estrutura". */
  readonly service: string;
  /** Como disciplina e serviço estavam separados: ` / `, ` - `, ou nenhum. */
  readonly separator: 'SLASH' | 'HYPHEN' | null;
  readonly location: TaskLocation;
}

const FACES: ReadonlyMap<string, FacadeFace> = new Map([
  ['frente', 'FRENTE'],
  ['fundos', 'FUNDOS'],
  ['lateral direita', 'LATERAL DIREITA'],
  ['lateral esquerda', 'LATERAL ESQUERDA'],
]);

/** `1º Pavimento - Tipo`, `14º Pavimento - Barrilete Teto`, `3º Pavimento`. */
const FLOOR_WITH_WORD = /^(\d+)\s*[º°o]\s*pavimento(?:\s*[-–—]\s*(.+))?$/i;
/** `14º Barrilete` — a grafia que perdeu a palavra "Pavimento". */
const FLOOR_WITHOUT_WORD = /^(\d+)\s*[º°o]\s*(barrilete(?:\s+teto)?)$/i;
/** `Muros 1º Pavimento`. */
const WALLS = /^muros?\s+(\d+)\s*[º°o]\s*pavimento$/i;
/** O hífen que separa disciplina de serviço tem espaço dos dois lados: "Guarda-Corpo" não é par. */
const HYPHEN_PAIR = /\s[-–—]\s/;

/**
 * @param name o nome da tarefa como veio no arquivo
 * @param groupPath os nomes dos grupos acima dela, do topo para baixo — só serve para
 *   reconhecer o padrão invertido da fundação
 */
export function splitTaskName(name: string, groupPath: readonly string[] = []): ParsedTaskName {
  const raw = name.replace(/\s+/g, ' ').trim();
  const parts = raw
    .split(/\s\/\s/)
    .map((part) => part.trim())
    .filter((part) => part !== '');

  if (parts.length <= 1) {
    return { raw, pattern: 'NO_LOCATION', ...splitLeft([raw]), location: { kind: 'NONE' } };
  }

  const last = parts[parts.length - 1] ?? '';
  const left = parts.slice(0, -1);
  const location = parseLocation(last);

  if (location === undefined) {
    // "Fundação / Execução de Gabarito": a esquerda é a disciplina, e o que está na direita
    // é o serviço. Só vale quando a esquerda é um dos grupos acima — senão é local que não
    // se reconhece, e quem decide é a conferência.
    const groups = new Set(groupPath.map(normalizeName));
    const leftText = left[0] ?? '';
    if (left.length === 1 && groups.has(normalizeName(leftText))) {
      return {
        raw,
        pattern: 'DISCIPLINE_SERVICE',
        discipline: leftText,
        service: last,
        separator: 'SLASH',
        location: { kind: 'NONE' },
      };
    }
  }

  return {
    raw,
    pattern: left.length >= 2 ? 'DISCIPLINE_SERVICE_LOCATION' : 'SERVICE_LOCATION',
    ...splitLeft(left),
    location: location ?? { kind: 'UNKNOWN', raw: last },
  };
}

/**
 * O que está à esquerda do local.
 *
 * Com duas partes ou mais, a primeira é a disciplina e o resto é o serviço, separados por
 * barra. Com uma só, ela é o serviço — salvo se trouxer o par com hífen
 * ("Instalação Hidráulica - Prumada & Distribuição"), que se lê como disciplina e serviço.
 */
function splitLeft(
  left: readonly string[],
): Pick<ParsedTaskName, 'discipline' | 'service' | 'separator'> {
  if (left.length >= 2) {
    return { discipline: left[0] ?? null, service: left.slice(1).join(' / '), separator: 'SLASH' };
  }
  const text = left[0] ?? '';
  const pair = HYPHEN_PAIR.exec(text);
  if (pair === null) return { discipline: null, service: text, separator: null };
  return {
    discipline: text.slice(0, pair.index).trim(),
    service: text.slice(pair.index + pair[0].length).trim(),
    separator: 'HYPHEN',
  };
}

/** Reconhece o texto do local; `undefined` quando não é nenhuma das espécies conhecidas. */
function parseLocation(text: string): TaskLocation | undefined {
  const face = FACES.get(normalizeName(text));
  if (face !== undefined) return { kind: 'FACADE', face, raw: text };

  const walls = WALLS.exec(text);
  if (walls !== null) return { kind: 'WALLS', level: Number(walls[1]), raw: text };

  const withWord = FLOOR_WITH_WORD.exec(text);
  if (withWord !== null) {
    return {
      kind: 'FLOOR',
      level: Number(withWord[1]),
      floorKind: withWord[2]?.trim() ?? null,
      raw: text,
    };
  }

  const withoutWord = FLOOR_WITHOUT_WORD.exec(text);
  if (withoutWord !== null) {
    return {
      kind: 'FLOOR',
      level: Number(withoutWord[1]),
      floorKind: titleCase(withoutWord[2] ?? ''),
      raw: text,
    };
  }

  if (normalizeName(text) === 'barrilete') {
    return { kind: 'UNNUMBERED_FLOOR', floorKind: 'Barrilete', raw: text };
  }
  if (normalizeName(text) === 'térreo' || normalizeName(text) === 'terreo') {
    return { kind: 'FLOOR', level: 0, floorKind: null, raw: text };
  }
  return undefined;
}

function titleCase(text: string): string {
  return text
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}
